import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const inputCls = "w-full rounded-xl border border-white/10 bg-[#0F1420] px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400";

export default function AdminSettings() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/admin/settings").then((r) => {
            const d = r.data;
            setForm({
                contact_email: d.contact_email || "",
                phone: d.phone || "",
                socials: { linkedin: "", instagram: "", facebook: "", x: "", ...(d.socials || {}) },
                budget_options: (d.budget_options || []).join("\n"),
                timeline_options: (d.timeline_options || []).join("\n"),
                ceo_bio: d.ceo_bio || "",
                notify_email: d.notify_email || "",
                email_notifications_enabled: d.email_notifications_enabled !== false,
                client_confirmation_enabled: d.client_confirmation_enabled !== false,
            });
        }).catch((e) => toast.error(errMsg(e)));
    }, []);

    if (!form) return <p className="py-20 text-center text-sm text-slate-500">Loading…</p>;

    const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.put("/admin/settings", {
                contact_email: form.contact_email.trim(),
                phone: form.phone.trim(),
                socials: form.socials,
                budget_options: lines(form.budget_options),
                timeline_options: lines(form.timeline_options),
                ceo_bio: form.ceo_bio,
                notify_email: form.notify_email.trim(),
                email_notifications_enabled: form.email_notifications_enabled,
                client_confirmation_enabled: form.client_confirmation_enabled,
            });
            toast.success("Settings saved — live on the site immediately.");
        } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
    };

    const setSocial = (k) => (e) => setForm({ ...form, socials: { ...form.socials, [k]: e.target.value } });

    return (
        <div className="max-w-3xl" data-testid="admin-settings-page">
            <h1 className="font-display text-2xl font-bold text-white">Settings</h1>
            <p className="mt-1 text-sm text-slate-400">Public contact details, inquiry form options, and company content. Only filled-in fields are shown on the site — empty fields stay hidden.</p>

            <form onSubmit={save} className="mt-8 space-y-8">
                <section className="rounded-2xl border border-white/10 bg-hive-card p-6 space-y-4" data-testid="settings-contact-section">
                    <h2 className="font-display font-bold text-white">Contact Information</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className={labelCls} htmlFor="st-email">Business Email</label>
                            <input id="st-email" type="email" data-testid="settings-email-input" className={inputCls} placeholder="hello@…" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="st-phone">Telephone</label>
                            <input id="st-phone" data-testid="settings-phone-input" className={inputCls} placeholder="(562) …" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500">These details appear on the Contact page and in the footer.</p>
                </section>

                <section className="rounded-2xl border border-white/10 bg-hive-card p-6 space-y-4" data-testid="settings-email-notifications-section">
                    <h2 className="font-display font-bold text-white">Email Notifications</h2>
                    <label className="flex items-center gap-3" htmlFor="st-notify-enabled">
                        <input id="st-notify-enabled" type="checkbox" data-testid="settings-notify-enabled-checkbox"
                            checked={form.email_notifications_enabled} onChange={(e) => setForm({ ...form, email_notifications_enabled: e.target.checked })}
                            className="h-4 w-4 rounded accent-violet-600" />
                        <span className="text-sm text-slate-300">Email me whenever a new inquiry arrives</span>
                    </label>
                    <label className="flex items-center gap-3" htmlFor="st-client-confirm">
                        <input id="st-client-confirm" type="checkbox" data-testid="settings-client-confirmation-checkbox"
                            checked={form.client_confirmation_enabled} onChange={(e) => setForm({ ...form, client_confirmation_enabled: e.target.checked })}
                            className="h-4 w-4 rounded accent-violet-600" />
                        <span className="text-sm text-slate-300">Send clients an automatic confirmation email with their reference number</span>
                    </label>
                    <div>
                        <label className={labelCls} htmlFor="st-notify-email">Notification Recipient</label>
                        <input id="st-notify-email" type="email" data-testid="settings-notify-email-input" className={inputCls}
                            placeholder="Defaults to the business email above" value={form.notify_email}
                            onChange={(e) => setForm({ ...form, notify_email: e.target.value })} />
                        <p className="mt-1.5 text-[11px] text-slate-500">Leave empty to send notifications to the business email above. Replying to a notification replies directly to the client.</p>
                    </div>
                </section>

                <section className="rounded-2xl border border-white/10 bg-hive-card p-6 space-y-4" data-testid="settings-socials-section">
                    <h2 className="font-display font-bold text-white">Social Profiles</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        {[["linkedin", "LinkedIn URL"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["x", "X (Twitter) URL"]].map(([k, label]) => (
                            <div key={k}>
                                <label className={labelCls} htmlFor={`st-${k}`}>{label}</label>
                                <input id={`st-${k}`} data-testid={`settings-social-${k}`} className={inputCls} placeholder="https://…" value={form.socials[k]} onChange={setSocial(k)} />
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-slate-500">Only verified profile URLs should be added. Empty fields are hidden from the footer.</p>
                </section>

                <section className="rounded-2xl border border-white/10 bg-hive-card p-6 space-y-4" data-testid="settings-form-options-section">
                    <h2 className="font-display font-bold text-white">Inquiry Form Options</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className={labelCls} htmlFor="st-budget">Budget Ranges (one per line)</label>
                            <textarea id="st-budget" rows={5} data-testid="settings-budget-options" className={inputCls} value={form.budget_options} onChange={(e) => setForm({ ...form, budget_options: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="st-timeline">Timeline Options (one per line)</label>
                            <textarea id="st-timeline" rows={5} data-testid="settings-timeline-options" className={inputCls} value={form.timeline_options} onChange={(e) => setForm({ ...form, timeline_options: e.target.value })} />
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl border border-white/10 bg-hive-card p-6 space-y-4" data-testid="settings-ceo-section">
                    <h2 className="font-display font-bold text-white">CEO Biography</h2>
                    <label className={labelCls} htmlFor="st-bio">Peter I. Castillo — biography shown on the About page</label>
                    <textarea id="st-bio" rows={6} data-testid="settings-ceo-bio" className={inputCls} placeholder="Write the CEO biography here…" value={form.ceo_bio} onChange={(e) => setForm({ ...form, ceo_bio: e.target.value })} />
                </section>

                <button type="submit" disabled={busy} data-testid="settings-save-button"
                    className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white hover:bg-violet-500 disabled:opacity-60">
                    <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Settings"}
                </button>
            </form>
        </div>
    );
}
